import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  FaMapMarkerAlt, 
  FaPhone, 
  FaEnvelope, 
  FaClock, 
  FaPaperPlane,
  FaDirections,
  FaWhatsapp
} from 'react-icons/fa';
import '../style.css';

const BookNow = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });

  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    // Simulate API call
    setTimeout(() => {
      setIsSubmitted(true);
      setIsLoading(false);
      
      // Reset form after 3 seconds
      setTimeout(() => {
        setFormData({ name: '', email: '', message: '' });
        setIsSubmitted(false);
      }, 3000);
    }, 1500);
  };

  const handleWhatsApp = () => {
    const message = `Hello! I'd like to book a cab.`;
    const whatsappUrl = `https://wa.me/+91 6307291567?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  return (
    <div className="book-now-container" id="book-now">
      {/* Header Section */}
      <motion.div 
        className="book-now-header"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <div className="header-content">
          <motion.span 
            className="header-badge"
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: "spring" }}
          >
           
          </motion.span>
          
          <motion.h1 
            className="header-title"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
          >
            Let's Start <span className="highlight-text">The Journey</span>
          </motion.h1>
          
          <motion.p 
            className="header-description"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
          >
            Have questions about your spiritual tour or need a custom taxi package? 
            Our team is here to assist.
          </motion.p>
        </div>
      </motion.div>

      {/* Main Content */}
      <div className="book-now-content-wrapper">
        <div className="content-grid">
          {/* Left Column - Form Section */}
          <motion.div 
            className="form-section"
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            {/* Response Time Card */}
            <motion.div 
              className="response-card"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.3 }}
            >
              <div className="response-icon-circle">
                <FaClock className="response-icon" />
              </div>
              <div className="response-details">
                <h3 className="response-title">Response Time</h3>
                <div className="response-main">
                  <span className="response-time">Under 15 Mins</span>
                  <span className="response-tag">24/7 Customer Care</span>
                </div>
              </div>
            </motion.div>

            {/* Contact Form */}
            <motion.div 
              className="form-card"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
            >
              <h3 className="form-title">Send us a Message</h3>
              
              <form className="contact-form" onSubmit={handleSubmit}>
                <div className="form-field">
                  <label htmlFor="name">Full Name</label>
                  <div className="input-group">
                    <input
                      type="text"
                      id="name"
                      name="name"
                      placeholder="vns tours and travels"
                      value={formData.name}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </div>

                <div className="form-field">
                  <label htmlFor="email">Email Address</label>
                  <div className="input-group">
                    <input
                      type="email"
                      id="email"
                      name="email"
                      placeholder="info@vnstour.com"
                      value={formData.email}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </div>

                <div className="form-field">
                  <label htmlFor="message">Your Message</label>
                  <div className="input-group">
                    <textarea
                      id="message"
                      name="message"
                      placeholder="Tell us about your travel plans..."
                      rows="4"
                      value={formData.message}
                      onChange={handleChange}
                      required
                    ></textarea>
                  </div>
                </div>

                <motion.button
                  type="submit"
                  className="submit-button"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <span className="loading-text">
                      <span className="loading-spinner"></span>
                      Sending...
                    </span>
                  ) : isSubmitted ? (
                    <>
                      <FaPaperPlane className="button-icon" />
                      Message Sent!
                    </>
                  ) : (
                    <>
                      <FaPaperPlane className="button-icon" />
                      SEND MESSAGE
                    </>
                  )}
                </motion.button>
              </form>
            </motion.div>

            {/* WhatsApp Quick Button */}
            <motion.button
              className="whatsapp-button"
              onClick={handleWhatsApp}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <FaWhatsapp className="whatsapp-icon" />
              <span> WhatsApp</span>
            </motion.button>
          </motion.div>

          {/* Right Column - Contact Info */}
          <motion.div 
            className="contact-section"
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            {/* Location Card */}
            <motion.div 
              className="contact-card"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              whileHover={{ y: -5 }}
            >
              <div className="contact-card-header">
                <div className="contact-icon-wrapper">
                  <FaMapMarkerAlt className="contact-card-icon" />
                </div>
                <h3 className="contact-card-title">Our Location</h3>
              </div>
              
              <div className="contact-card-body">
                <p className="address-text">
                  G.T road Mohansarai bypas Rajatalab Varanasi UP-221302
                </p>
                
                <motion.a 
                  href="https://www.bing.com/maps/geoplat/REST/v1/Imagery/Map/RoadVibrant/25.31071,82.870167/12?ms=648,281&sv=9.44&heid=7419163818078502913,707070&ml=Basemap,LandCover,Landmarks,OsmBuildings&key=AnTcaqBi2ypp0xI-OZNi4W_ik2KhjgpqioTAtXLC8GzkMBQRMlyxvxyTnd5b73im&c=en-US&ur=in&fmt=jpeg&od=1&shading=hill&logo=n&da=ro"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="directions-button"
                  whileHover={{ x: 5 }}
                >
                  <FaDirections className="button-icon" />
                  Get Directions
                </motion.a>
              </div>
            </motion.div>

            {/* Contact Information */}
            <motion.div 
              className="contact-info-card"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              whileHover={{ y: -5 }}
            >
              <h3 className="contact-info-title">Contact Information</h3>
              
              <div className="contact-list">
                {/* Email */}
                <div className="contact-item">
                  <div className="contact-item-icon">
                    <FaEnvelope />
                  </div>
                  <div className="contact-item-content">
                    <span className="contact-item-label">Email us at</span>
                    <a href="mailto:cabservicevaranasi44@gmail.com" className="contact-item-value">
                     vnstoursandtravels9696@gmail.com

                    </a>
                  </div>
                </div>

                {/* Primary Support */}
                <div className="contact-item">
                  <div className="contact-item-icon">
                    <FaPhone />
                  </div>
                  <div className="contact-item-content">
                    <span className="contact-item-label">Primary Support</span>
                    <a href="tel:6307291567" className="contact-item-value">
                     6307291567
                    </a>
                  </div>
                </div>

                {/* Secondary Line */}
                <div className="contact-item">
                  <div className="contact-item-icon">
                    <FaPhone />
                  </div>
                  <div className="contact-item-content">
                    <span className="contact-item-label">Secondary Line</span>
                    <a href="tel:6307291567" className="contact-item-value">
                     6307291567
                    </a>
                  </div>
                </div>
              </div>

              {/* Call Now Button */}
              <motion.a 
                href="tel:6307291567"
                className="call-button"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <FaPhone className="button-icon" />
                Call Now
              </motion.a>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default BookNow;